import { categoryPath } from '../seo/metadata';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Category } from '../types';
import styles from './styles/Navbar.module.css';
import { useCart } from '../context/CartContext';
import { FaSearch, FaShoppingCart, FaChevronDown, FaBars, FaTimes, FaWhatsapp } from 'react-icons/fa';
import logo from '../logo.png';
import { DEFAULT_NAVBAR_CONFIG, NavbarLinkConfig, NavbarLinkKey, normalizeNavbarConfig } from '../data/navbarConfig';

// Definimos una interfaz ligera para el buscador
interface ProductSearchItem {
    idProductos: number;
    nombre: string;
    categoria: string;
    subcategoria?: string;
    tags?: string;
    marca?: string;
    descripcion?: string;
    // Agrega 'url_imagen' si quieres mostrar foto pequeña en el buscador
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const MOBILE_MEDIA_QUERY = '(max-width: 1023px)';

const Navbar: React.FC = () => {
    // Estados generales
    const [categories, setCategories] = useState<Category[]>([]);
    const { totalItems } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const navbarRef = useRef<HTMLElement>(null);
    const previousLocationRef = useRef(`${location.pathname}${location.search}`);

    // Estados para la búsqueda
    const [allProducts, setAllProducts] = useState<ProductSearchItem[]>([]); // Base de datos local
    const [filteredResults, setFilteredResults] = useState<ProductSearchItem[]>([]); // Resultados a mostrar
    const [searchTerm, setSearchTerm] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [openMobileDropdown, setOpenMobileDropdown] = useState<NavbarLinkKey | null>(null);
    const [productDropdownDismissed, setProductDropdownDismissed] = useState(false);
    const [navbarConfig, setNavbarConfig] = useState<NavbarLinkConfig[]>(DEFAULT_NAVBAR_CONFIG);

    // --- EFECTOS ---

    useEffect(() => {
        const loadCategories = () => {
            fetch(`${API_URL}/menu`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) setCategories(data);
                    else console.error("Menu API did not return an array", data);
                })
                .catch(err => console.error("Error cargando menú", err));
        };

        // 2. Cargar índice de búsqueda (Carga única ligera)
        // Asegúrate de crear este endpoint en tu Flask que devuelva {id, nombre, categoria}
        fetch(`${API_URL}/search-index`)
            .then(res => res.json())
            .then(data => setAllProducts(data))
            .catch(err => console.error("Error cargando índice de búsqueda", err));

        const loadNavbarConfig = () => {
            fetch(`${API_URL}/navbar-config`)
                .then(res => res.ok ? res.json() : Promise.reject(new Error('Navbar config unavailable')))
                .then(data => setNavbarConfig(normalizeNavbarConfig(data.links)))
                .catch(err => console.error('Error cargando configuracion del navbar', err));
        };
        const handleNavbarUpdate = (event: Event) => {
            const detail = (event as CustomEvent).detail;
            if (detail) setNavbarConfig(normalizeNavbarConfig(detail));
            else loadNavbarConfig();
        };

        loadCategories();
        loadNavbarConfig();
        window.addEventListener('navbar-config-updated', handleNavbarUpdate);
        window.addEventListener('categories-config-updated', loadCategories);
        return () => {
            window.removeEventListener('navbar-config-updated', handleNavbarUpdate);
            window.removeEventListener('categories-config-updated', loadCategories);
        };
    }, []);

    useEffect(() => {
        const currentLocation = `${location.pathname}${location.search}`;
        if (previousLocationRef.current === currentLocation) return;
        previousLocationRef.current = currentLocation;
        setIsMobileMenuOpen(false);
        setOpenMobileDropdown(null);
        setProductDropdownDismissed(true);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

        const closeMobileMenu = () => {
            setIsMobileMenuOpen(false);
            setOpenMobileDropdown(null);
        };
        const handlePointerDown = (event: PointerEvent) => {
            if (isMobileMenuOpen && !navbarRef.current?.contains(event.target as Node)) {
                closeMobileMenu();
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeMobileMenu();
        };
        const handleViewportChange = () => closeMobileMenu();

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        mobileQuery.addEventListener('change', handleViewportChange);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            mobileQuery.removeEventListener('change', handleViewportChange);
        };
    }, [isMobileMenuOpen]);

    // --- HANDLERS ---

    // Búsqueda en vivo (Live Search)
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);

        if (term.trim().length > 0) {
            // Filtramos el array local en memoria (¡Rapidísimo!)
            const normalizedTerm = term.trim().toLowerCase();
            const results = allProducts.filter(product => {
                const searchableText = [
                    product.nombre,
                    product.categoria,
                    product.subcategoria,
                    product.tags,
                    product.marca,
                    product.descripcion
                ].filter(Boolean).join(' ').toLowerCase();
                return searchableText.includes(normalizedTerm);
            }).sort((a, b) => {
                const aName = a.nombre.toLowerCase();
                const bName = b.nombre.toLowerCase();
                const aPriority = aName === normalizedTerm ? 0 : aName.startsWith(normalizedTerm) ? 1 : 2;
                const bPriority = bName === normalizedTerm ? 0 : bName.startsWith(normalizedTerm) ? 1 : 2;
                return aPriority - bPriority || a.nombre.localeCompare(b.nombre);
            });
            setFilteredResults(results);
        } else {
            setFilteredResults([]);
        }
    };

    // Búsqueda al dar ENTER (Búsqueda tradicional)
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            navigate(`/productos?search=${encodeURIComponent(searchTerm)}`);
            setFilteredResults([]); // Limpiamos sugerencias
            setIsMobileMenuOpen(false);
        }
    };

    // Al hacer clic en una sugerencia
    const handleSuggestionClick = () => {
        setSearchTerm('');      // Opcional: limpiar el input
        setFilteredResults([]); // Cerrar lista
        setIsMobileMenuOpen(false);
    };

    const handleNavClick = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('a')) {
            setIsMobileMenuOpen(false);
            setOpenMobileDropdown(null);
            if (target.closest(`.${styles['products-dropdown-menu']}`)) setProductDropdownDismissed(true);
        }
    };

    const handleDropdownClick = (event: React.MouseEvent<HTMLAnchorElement>, key: NavbarLinkKey) => {
        if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
        event.preventDefault();
        event.stopPropagation();
        setOpenMobileDropdown(current => current === key ? null : key);
    };

    const navbarItemProps = (key: NavbarLinkKey) => {
        const config = navbarConfig.find(link => link.key === key);
        return {
            hidden: config?.visible === false,
            style: { order: config?.orden ?? 0 }
        };
    };

    const dropdownDirectionClass = (key: NavbarLinkKey) => {
        const visibleLinks = navbarConfig
            .filter(link => link.visible !== false)
            .slice()
            .sort((a, b) => a.orden - b.orden);
        const position = visibleLinks.findIndex(link => link.key === key);
        const opensToLeft = position >= 0 && position >= Math.ceil(visibleLinks.length / 2);
        return opensToLeft
            ? styles['dropdown-opens-left']
            : styles['dropdown-opens-right'];
    };

    return (
        <header ref={navbarRef} className={`${styles['navbar-container']} ${productDropdownDismissed ? styles['product-dropdown-dismissed'] : ''}`}>
            {/* BARRA PRINCIPAL */}
            <div className={styles['main-header']}>
                <div className={`${styles.container} ${styles['main-header-content']}`}>
                    <div className={styles['search-row']}>
                        <div className={`${styles['search-wrapper']} ${styles.active}`}>
                            <form onSubmit={handleSearchSubmit} className={styles['search-form']}>
                                <input
                                    type="text"
                                    className={styles['search-input']}
                                    placeholder="Buscar productos..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    aria-label="Buscar productos"
                                />
                                <button type="submit" className={styles['search-submit']} aria-label="Buscar">
                                    <FaSearch />
                                </button>

                                {searchTerm.trim() && filteredResults.length > 0 && (
                                    <div className={styles['search-suggestions']}>
                                        <ul>
                                            {filteredResults.slice(0, 8).map(prod => (
                                                <li key={prod.idProductos}>
                                                    <Link
                                                        to={`/producto/${prod.idProductos}`}
                                                        onClick={handleSuggestionClick}
                                                    >
                                                        <span className={styles['suggestion-name']}>{prod.nombre}</span>
                                                        <span className={styles['suggestion-cat']}>{prod.categoria}</span>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* Logo */}
                    <Link to="/" className={styles.logo}>
                        <img src={logo} alt="Familia Garcia Vidal" />
                    </Link>

                    {/* Menú de Navegación */}
                    <button
                        type="button"
                        className={styles['mobile-menu-toggle']}
                        onClick={() => {
                            setIsMobileMenuOpen(open => {
                                const willOpen = !open;
                                if (!willOpen) setOpenMobileDropdown(null);
                                return willOpen;
                            });
                        }}
                        aria-label={isMobileMenuOpen ? 'Cerrar menu' : 'Abrir menu'}
                        aria-expanded={isMobileMenuOpen}
                        aria-controls="main-navigation"
                    >
                        {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
                    </button>

                    <nav
                        id="main-navigation"
                        className={`${styles['main-nav']} ${isMobileMenuOpen ? styles['main-nav-open'] : ''}`}
                        onClick={handleNavClick}
                    >
                        <ul className={styles['nav-links']}>
                            <li className={`${styles['nav-item']} ${styles['mobile-home-item']}`}>
                                <Link to="/" className={styles['nav-link']}>INICIO</Link>
                            </li>
                            <li className={`${styles['nav-item']} ${styles['dropdown-parent']} ${dropdownDirectionClass('familia')} ${openMobileDropdown === 'familia' ? styles['mobile-dropdown-open'] : ''}`} {...navbarItemProps('familia')}>
                                <Link to="/familia" className={`${styles['nav-link']} ${styles['main-link']}`} onClick={event => handleDropdownClick(event, 'familia')} aria-expanded={openMobileDropdown === 'familia'}>
                                    FAMILIA <FaChevronDown size={12} style={{ marginLeft: '4px' }} />
                                </Link>
                                <div className={`${styles['dropdown-menu']} ${styles['family-dropdown-menu']}`}>
                                    <ul className={styles['family-link-list']}>
                                        <li><Link to="/familia">Sumate a la familia</Link></li>
                                        <li><Link to="/nosotros">Nosotros</Link></li>
                                        <li><Link to="/credo">Nuestro Credo</Link></li>
                                    </ul>
                                </div>
                            </li>

                            <li className={`${styles['nav-item']} ${styles['dropdown-parent']} ${styles['products-dropdown-parent']} ${dropdownDirectionClass('productos')} ${openMobileDropdown === 'productos' ? styles['mobile-dropdown-open'] : ''}`} onMouseLeave={() => setProductDropdownDismissed(false)} {...navbarItemProps('productos')}>
                                <Link to="/productos" className={`${styles['nav-link']} ${styles['main-link']}`} onClick={event => handleDropdownClick(event, 'productos')} aria-expanded={openMobileDropdown === 'productos'}>
                                    PRODUCTOS <FaChevronDown size={12} style={{ marginLeft: '4px' }} />
                                </Link>
                                <div className={`${styles['dropdown-menu']} ${styles['products-dropdown-menu']}`}>
                                    <ul className={styles['category-list']}>
                                        {categories.filter(cat => cat.nombre.trim().toLocaleLowerCase('es-AR') !== 'todos los productos').map(cat => (
                                            <li key={cat.idCategoria} className={styles['category-item']}>
                                                <Link
                                                    to={categoryPath(cat.nombre)}
                                                    className={styles['navbar-category-link']}
                                                >
                                                    {cat.nombre}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </li>
                            <li className={styles['nav-item']} {...navbarItemProps('envios')}><Link to="/envios" className={styles['nav-link']}>Envío a domicilio</Link></li>
                            <li className={styles['nav-item']} {...navbarItemProps('locales')}><Link to="/locales" className={styles['nav-link']}>Locales</Link></li>

                        </ul>
                    </nav>

                    {/* Acciones */}
                    <div className={styles['header-actions']}>
                        {/* Carrito */}
                        <button
                            onClick={() => navigate('/checkout')}
                            className={`${styles['cart-btn']} ${totalItems > 0 ? styles['active-cart'] : ''}`}
                            aria-label={`Ir al carrito${totalItems > 0 ? `, ${totalItems} productos` : ''}`}
                        >
                            <FaShoppingCart size={20} /> {totalItems > 0 && <span className={styles['cart-badge']}>{totalItems}</span>}
                        </button>
                    </div>
                </div>
            </div>

            <a
                className={styles['whatsapp-btn']}
                href="https://wa.me/5493413080099?text=Hola%2C%20quiero%20hacer%20una%20consulta"
                target="_blank"
                rel="noreferrer"
                aria-label="Consultar por WhatsApp"
                title="Consultar por WhatsApp"
            >
                <FaWhatsapp aria-hidden="true" />
            </a>
        </header>
    );
};

export default Navbar;
